import { useStore } from '../store/useStore'
import { StatCard, Card, Badge, ProgressBar, PageHeader, Button } from '../components/UI'
import { format } from 'date-fns'

const C = { mint: '#00C896', red: '#FF4D6A', yellow: '#FFD166', blue: '#4DAAFF', purple: '#A78BFA', text: '#E8F5EE', muted: '#6B8F7A', border: '#1E2E24', surfaceAlt: '#161F1A' }

// Mock data — replace with real Plaid/Supabase data
const MOCK = {
  netWorth: 84320.45,
  monthlyIncome: 7200,
  monthlySpend: 4830.22,
  savingsRate: 32.9,
  creditScore: 748,
  accounts: [
    { name: 'Chase Checking', type: 'bank', balance: 5420.11, icon: '🏦' },
    { name: 'HYSA - Marcus', type: 'savings', balance: 22100.00, icon: '💰' },
    { name: 'Fidelity 401(k)', type: 'investment', balance: 48300.34, icon: '📈' },
    { name: 'Robinhood', type: 'investment', balance: 8500.00, icon: '🤖' },
  ],
  spending: [
    { category: 'Housing', amount: 1800, budget: 1800, color: C.blue },
    { category: 'Food & Dining', amount: 620, budget: 500, color: C.red },
    { category: 'Transport', amount: 210, budget: 250, color: C.mint },
    { category: 'Entertainment', amount: 145, budget: 200, color: C.purple },
    { category: 'Health', amount: 95, budget: 150, color: C.mint },
    { category: 'Shopping', amount: 380, budget: 300, color: C.red },
  ],
  transactions: [
    { desc: 'Whole Foods Market', amount: -87.42, date: new Date(), cat: 'Food' },
    { desc: 'Netflix', amount: -15.49, date: new Date(), cat: 'Entertainment' },
    { desc: 'Direct Deposit', amount: 3600.00, date: new Date(Date.now()-86400000), cat: 'Income' },
    { desc: 'Shell Gas Station', amount: -52.10, date: new Date(Date.now()-172800000), cat: 'Transport' },
    { desc: 'Amazon', amount: -143.22, date: new Date(Date.now()-259200000), cat: 'Shopping' },
  ],
  aiInsights: [
    { type: 'warning', msg: 'Food & Dining is 24% over budget. You\'ve spent $120 more than usual this month.', icon: '⚡', color: C.yellow },
    { type: 'tip', msg: 'Moving $500 more to your HYSA could earn an extra $23/month at current rates.', icon: '💡', color: C.blue },
    { type: 'win', msg: 'Your savings rate of 32.9% is in the top 15% of users your age!', icon: '🏆', color: C.mint },
  ],
}

export default function Dashboard() {
  const { user, plan } = useStore()
  const name = user?.email?.split('@')[0] || 'there'

  return (
    <div>
      <PageHeader
        title={`Good ${getGreeting()}, ${name} 👋`}
        subtitle={format(new Date(), 'EEEE, MMMM d, yyyy')}
        action={<Button onClick={() => window.location.href = '/accounts'}>+ Connect Account</Button>}
      />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard label="Net Worth" value={`$${MOCK.netWorth.toLocaleString()}`} sub="↑ $1,240 this month" accent={C.mint} />
        <StatCard label="Monthly Spend" value={`$${MOCK.monthlySpend.toLocaleString()}`} sub="▲ 8% vs last month" subColor={C.red} accent={C.red} />
        <StatCard label="Savings Rate" value={`${MOCK.savingsRate}%`} sub="Top 15% of users" accent={C.yellow} />
        <StatCard label="Credit Score" value={MOCK.creditScore} sub="↑ +12 pts this month" accent={C.purple} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        {/* Budget */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ color: C.text, fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700 }}>Budget Tracker</h3>
            <span style={{ color: C.muted, fontSize: 11 }}>{format(new Date(), 'MMM yyyy')}</span>
          </div>
          {MOCK.spending.map(s => (
            <div key={s.category} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: C.text, fontSize: 13 }}>{s.category}</span>
                <span style={{ fontSize: 12, color: s.amount > s.budget ? C.red : C.muted }}>
                  ${s.amount} <span style={{ color: C.muted }}>/ ${s.budget}</span>
                  {s.amount > s.budget && <span style={{ color: C.red, marginLeft: 6 }}>Over!</span>}
                </span>
              </div>
              <ProgressBar value={s.amount} max={s.budget} color={s.color} />
            </div>
          ))}
        </Card>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Accounts */}
          <Card>
            <h3 style={{ color: C.text, fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Accounts</h3>
            {MOCK.accounts.map((a, i) => (
              <div key={a.name} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: i < MOCK.accounts.length - 1 ? `1px solid ${C.border}` : 'none'
              }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 20 }}>{a.icon}</span>
                  <div>
                    <div style={{ color: C.text, fontSize: 13 }}>{a.name}</div>
                    <div style={{ color: C.muted, fontSize: 11, textTransform: 'capitalize' }}>{a.type}</div>
                  </div>
                </div>
                <div style={{ color: C.text, fontWeight: 600, fontFamily: 'Syne, sans-serif' }}>
                  ${a.balance.toLocaleString('en', { minimumFractionDigits: 2 })}
                </div>
              </div>
            ))}
          </Card>

          {/* AI Insights */}
          <Card style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ color: C.mint }}>✦</span>
              <h3 style={{ color: C.text, fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700 }}>AI Insights</h3>
              {plan === 'free' && <Badge color={C.muted}>Limited</Badge>}
            </div>
            {MOCK.aiInsights.map((ins, i) => (
              <div key={i} style={{
                display: 'flex', gap: 12, padding: '12px 14px',
                background: C.surfaceAlt, borderRadius: 10,
                border: `1px solid ${ins.color}22`, marginBottom: 8,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{ins.icon}</span>
                <p style={{ color: C.text, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{ins.msg}</p>
              </div>
            ))}
            {plan === 'free' && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <a href="/pricing" style={{ color: C.mint, fontSize: 12, textDecoration: 'none' }}>
                  Upgrade for unlimited AI insights →
                </a>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Transactions */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: C.text, fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700 }}>Recent Transactions</h3>
          <a href="/accounts" style={{ color: C.mint, fontSize: 12, textDecoration: 'none' }}>View all →</a>
        </div>
        {MOCK.transactions.map((t, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '11px 0', borderBottom: i < MOCK.transactions.length - 1 ? `1px solid ${C.border}` : 'none'
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: C.surfaceAlt,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, color: C.muted, border: `1px solid ${C.border}`,
              }}>{t.cat[0]}</div>
              <div>
                <div style={{ color: C.text, fontSize: 13 }}>{t.desc}</div>
                <div style={{ color: C.muted, fontSize: 11 }}>{format(t.date, 'MMM d')} · {t.cat}</div>
              </div>
            </div>
            <span style={{ color: t.amount > 0 ? C.mint : C.text, fontWeight: 600, fontFamily: 'Syne, sans-serif' }}>
              {t.amount > 0 ? '+' : ''}${Math.abs(t.amount).toFixed(2)}
            </span>
          </div>
        ))}
      </Card>
    </div>
  )
}

function getGreeting() {
  const h = new Date().getHours()
  if (h < 12) return 'morning'
  if (h < 17) return 'afternoon'
  return 'evening'
}
